import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataStore } from '../store/dataStore';
import { useAuthStore } from '../store/authStore';
import { formatDate, appointmentTypes, shootStatuses } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Users,
  Calendar,
  Camera,
  Clock,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Heart,
  Download,
  Image,
  Check,
  Upload,
  X,
  Printer
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

// Tarih ve saat formatı: Gün Ay Yıl Saat:Dakika
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { dashboard, dashboardLoading, fetchDashboard, customers, fetchCustomers, activeUploads, cancelUpload } = useDataStore();
  const { token } = useAuthStore();
  const [processingAlbum, setProcessingAlbum] = useState(null);

  // Aktif yüklemeleri filtrele
  const activeUploadList = Object.entries(activeUploads).filter(([_, upload]) => 
    !upload.cancelled && upload.current < upload.total
  );

  useEffect(() => {
    fetchDashboard();
    fetchCustomers();
  }, [fetchDashboard, fetchCustomers]);

  // Yaklaşan düğünleri hesapla (müşteri listesinden)
  const upcomingWeddings = customers
    .filter(customer => {
      if (!customer.wedding_date) return false;
      const weddingDate = new Date(customer.wedding_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((weddingDate - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0; // sadece gelecek işler
    })
    .map(customer => {
      const weddingDate = new Date(customer.wedding_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((weddingDate - today) / (1000 * 60 * 60 * 24));
      return { ...customer, daysLeft: diffDays };
    })
    .sort((a, b) => a.daysLeft - b.daysLeft)
    .slice(0, 5); // En yakın 5 düğün

  // İşleme alma fonksiyonu
  const handleMarkProcessed = async (albumId) => {
    setProcessingAlbum(albumId);
    try {
      const res = await fetch(`${API}/api/albums/${albumId}/process`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('İşleme alınamadı');
      
      toast.success('Albüm işleme alındı olarak işaretlendi');
      fetchDashboard(); // Dashboard'u yenile
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingAlbum(null);
    }
  };

  if (dashboardLoading || !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const stats = [
    {
      title: 'Toplam Müşteri',
      value: dashboard.total_customers,
      icon: Users,
      color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30'
    },
    {
      title: 'Toplam Randevu',
      value: dashboard.total_appointments,
      icon: Calendar,
      color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30'
    },
    {
      title: 'Toplam Çekim',
      value: dashboard.total_shoots,
      icon: Camera,
      color: 'text-green-600 bg-green-100 dark:bg-green-900/30'
    },
    {
      title: 'Bekleyen Teslim',
      value: dashboard.shoot_status_counts?.editing || 0,
      icon: Clock,
      color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30'
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Hoş geldiniz! İşte genel durumunuz.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={stat.title} className={`animate-fade-in stagger-${index + 1}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Aktif Yüklemeler */}
      {activeUploadList.length > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Upload className="h-5 w-5 text-primary animate-pulse" />
              Aktif Yüklemeler ({activeUploadList.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeUploadList.map(([albumId, upload]) => (
              <div key={albumId} className="bg-background rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-sm">{upload.customerName}</p>
                    <p className="text-xs text-muted-foreground">{upload.albumName}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm">
                      {upload.current} / {upload.total}
                    </span>
                    {upload.estimatedTimeLeft && (
                      <span className="text-xs text-muted-foreground">
                        Kalan: {upload.estimatedTimeLeft}
                      </span>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      onClick={() => {
                        if (window.confirm('Yüklemeyi iptal etmek istiyor musunuz?')) {
                          cancelUpload(albumId);
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${upload.percent}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="h-auto p-0 text-xs"
                    onClick={() => navigate(`/panel/album/${upload.customerId}`)}
                  >
                    Albüme Git →
                  </Button>
                  <span className="text-xs text-muted-foreground">%{upload.percent}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-1 gap-6">
        {/* Yaklaşan Çekimler */}
        <Card className="animate-fade-in stagger-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary" />
              Yaklaşan Çekimler
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingWeddings.length > 0 ? (
              <div className="space-y-3">
                {upcomingWeddings.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate(`/panel/musteri/${customer.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-2 w-2 rounded-full ${
                        customer.daysLeft <= 3 ? 'bg-red-500' : 
                        customer.daysLeft <= 7 ? 'bg-amber-500' : 'bg-green-500'
                      }`} />
                      <div>
                        <p className="font-medium text-sm">{customer.bride_name} & {customer.groom_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {customer.event_time_start || '18:00'} • {customer.bride_phone}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      customer.daysLeft === 0 ? 'bg-red-500/20 text-red-600' :
                      customer.daysLeft <= 3 ? 'bg-amber-500/20 text-amber-600' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {customer.daysLeft === 0 ? 'Bugün!' : 
                       customer.daysLeft === 1 ? 'Yarın' : 
                       `${customer.daysLeft} gün`}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Camera className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Yaklaşan çekim yok</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Selection Completed */}
      {dashboard.selection_completed?.length > 0 && (
        <Card className="animate-fade-in border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-green-600 fill-green-600" />
              Seçim Tamamlananlar
              <span className="ml-auto text-sm font-normal text-muted-foreground">
                {dashboard.selection_completed.length} müşteri
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.selection_completed.map((item) => (
                <div
                  key={item.album_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{item.customer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.album_name} • {item.selected_count}/{item.photo_count} fotoğraf seçildi
                      </p>
                      {item.confirmed_at && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Onaylandı: {formatDateTime(item.confirmed_at)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 ml-14 sm:ml-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/panel/album/${item.customer_id}`)}
                      className="border-green-300 hover:bg-green-100 dark:border-green-700 dark:hover:bg-green-800"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      Albüm
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${RUNTIME_BACKEND_URL}/api/albums/${item.album_id}/download-selected`;
                        link.download = 'secilen_fotograflar.zip';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      İndir
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleMarkProcessed(item.album_id)}
                      disabled={processingAlbum === item.album_id}
                      data-testid={`process-album-${item.album_id}`}
                    >
                      {processingAlbum === item.album_id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      İşleme Al
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shoot Status Overview */}
      <Card className="animate-fade-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Çekim Durumu Özeti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-5 gap-4">
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Planlandı</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                {dashboard.planned_future || 0}
              </p>
              <p className="text-[10px] text-blue-600/60 mt-0.5">Tarihi gelmemiş</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Çekimde</span>
              </div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                {dashboard.today_shoots || 0}
              </p>
              <p className="text-[10px] text-amber-600/60 mt-0.5">Bugünkü çekimler</p>
            </div>
            <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700 dark:text-purple-400">Düzenleniyor</span>
              </div>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                {dashboard.album_status_counts?.processing || 0}
              </p>
              <p className="text-[10px] text-purple-600/60 mt-0.5">İşleme alınan</p>
            </div>
            <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2 mb-2">
                <Printer className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700 dark:text-orange-400">Baskıda</span>
              </div>
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                {dashboard.album_status_counts?.printing || 0}
              </p>
              <p className="text-[10px] text-orange-600/60 mt-0.5">Baskı aşamasında</p>
            </div>
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">Teslim Edildi</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                {dashboard.album_status_counts?.completed || 0}
              </p>
              <p className="text-[10px] text-green-600/60 mt-0.5">Tamamlanan</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
